import type { TFunction } from "i18next";

import i18n, { DEFAULT_NAMESPACE } from "@/i18n";

import {
  findTranslationKey,
  getTranslationIndex,
} from "@/Utils/translationIndex";

export interface BackendTranslationOptions {
  namespace?: string;
  keyPrefix?: string;
}

const getNamespace = (options: BackendTranslationOptions) =>
  options.namespace ?? DEFAULT_NAMESPACE;

/**
 * Translates a controlled backend display value while preserving arbitrary
 * backend content when no locale match exists.
 *
 * Backend values may already be i18next keys (for example, "active") or may
 * be legacy English labels (for example, "Active"). The latter are resolved
 * through the English locale as a compatibility bridge.
 */
export function translateBackendText(
  value: string | null | undefined,
  t: TFunction,
  options: BackendTranslationOptions = {},
): string | null | undefined {
  if (value == null || !value.trim()) {
    return value;
  }

  const namespace = getNamespace(options);
  const language = i18n.resolvedLanguage ?? i18n.language;

  if (
    i18n.exists(value, {
      lng: language,
      ns: namespace,
      keyPrefix: options.keyPrefix,
    })
  ) {
    const directTranslation = t(value, {
      ns: namespace,
      keyPrefix: options.keyPrefix,
      defaultValue: value,
    });

    return typeof directTranslation === "string" ? directTranslation : value;
  }

  const englishResource = i18n.getResourceBundle("en", namespace);
  const translationKey = findTranslationKey(
    value,
    getTranslationIndex(englishResource),
    options,
  );

  if (!translationKey) {
    return value;
  }

  const translatedValue = t(translationKey, {
    ns: namespace,
    defaultValue: value,
  });

  return typeof translatedValue === "string" &&
    translatedValue !== translationKey
    ? translatedValue
    : value;
}

type TranslationPathValue = Record<string, unknown> | unknown[];

const isObjectValue = (value: unknown): value is TranslationPathValue =>
  (typeof value === "object" && value !== null && !Array.isArray(value)) ||
  Array.isArray(value);

function translatePath(
  value: unknown,
  path: readonly string[],
  t: TFunction,
  options: BackendTranslationOptions,
): unknown {
  if (path.length === 0) {
    return typeof value === "string"
      ? translateBackendText(value, t, options)
      : value;
  }

  if (!isObjectValue(value)) {
    return value;
  }

  const [segment, ...remainingPath] = path;

  if (Array.isArray(value)) {
    const itemPath = segment === "*" ? remainingPath : path;
    return value.map((item) => translatePath(item, itemPath, t, options));
  }

  if (segment === "*") {
    return Object.fromEntries(
      Object.entries(value).map(([key, childValue]) => [
        key,
        translatePath(childValue, remainingPath, t, options),
      ]),
    );
  }

  if (!Object.prototype.hasOwnProperty.call(value, segment)) {
    return value;
  }

  return {
    ...value,
    [segment]: translatePath(value[segment], remainingPath, t, options),
  };
}

/**
 * Translates selected leaves in nested backend response objects.
 *
 * Examples: ["label"], ["tabs.*.label"], ["sections.*.fields.*.heading"].
 * Paths are opt-in so names, descriptions, notes, and other user content are
 * never translated just because they happen to be strings.
 */
export function translateBackendFields<T>(
  value: T,
  t: TFunction,
  paths: readonly string[],
  options: BackendTranslationOptions = {},
): T {
  return paths.reduce<unknown>(
    (currentValue, path) =>
      translatePath(currentValue, path ? path.split(".") : [], t, options),
    value,
  ) as T;
}
